import Papa from 'papaparse'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { petTypes, dogBreedList, catBreedList } from './breedAndPetTypeUtils'

dayjs.extend(customParseFormat)

export const MAX_ROWS_PER_FILE = 2000

export const CLIENT_COLUMNS = [
	'owner_id',
	'first_name',
	'last_name',
	'email',
	'phone',
	'address',
	'address_2',
	'city',
	'state',
	'zip'
]

export const PET_COLUMNS = [
	'owner_id',
	'pet_name',
	'species',
	'breed',
	'birthday',
	'sex',
	'weight_lbs',
	'medical_info',
	'grooming_info',
	'personality'
]

const REQUIRED_CLIENT_COLUMNS = ['owner_id', 'first_name']
const REQUIRED_PET_COLUMNS = ['owner_id', 'pet_name', 'species']

// --- Header normalization ---

// Normalizes "First Name", "first_name", " FIRST_NAME " all to "first_name"
function normalizeHeader(header) {
	return String(header || '')
		.trim()
		.toLowerCase()
		.replace(/\s+/g, '_')
		.replace(/_+/g, '_')
}

// --- Field-level helpers ---

function trimOrNull(value) {
	if (value === null || value === undefined) return null
	const s = String(value).trim()
	return s.length === 0 ? null : s
}

function isValidEmail(email) {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

// Returns digits-only string, or null if too short / too long after stripping
function digitsOnly(phone) {
	if (!phone) return null
	const d = String(phone).replace(/\D/g, '')
	return d
}

function formatPhone(phone) {
	const d = digitsOnly(phone)
	if (!d || d.length !== 10) return null
	return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

function normalizeSpecies(species) {
	if (!species) return null
	const s = String(species).trim().toLowerCase()
	const match = petTypes.find(p => p.toLowerCase() === s)
	return match || null
}

function normalizeSex(sex) {
	if (!sex) return null
	const s = String(sex).trim().toLowerCase()
	if (s === 'm' || s === 'male') return 'Male'
	if (s === 'f' || s === 'female') return 'Female'
	return null
}

function normalizeBreed(breed, canonicalSpecies) {
	if (!breed) return null
	const trimmed = String(breed).trim()
	if (!trimmed) return null
	const list =
		canonicalSpecies === 'Dog'
			? dogBreedList
			: canonicalSpecies === 'Cat'
				? catBreedList
				: []
	const match = list.find(b => b.toLowerCase() === trimmed.toLowerCase())
	return match || trimmed // pass through unknown breeds; warning will be raised
}

function isKnownBreed(breed, canonicalSpecies) {
	if (!breed) return true
	const list =
		canonicalSpecies === 'Dog'
			? dogBreedList
			: canonicalSpecies === 'Cat'
				? catBreedList
				: []
	return list.some(b => b.toLowerCase() === String(breed).trim().toLowerCase())
}

// Accepts YYYY-MM-DD, MM/DD/YYYY, M/D/YYYY → returns YYYY-MM-DD or null
function normalizeBirthday(value) {
	if (!value) return null
	const s = String(value).trim()
	if (!s) return null
	const formats = ['YYYY-MM-DD', 'YYYY/MM/DD', 'MM/DD/YYYY', 'M/D/YYYY', 'MM-DD-YYYY']
	for (const fmt of formats) {
		const d = dayjs(s, fmt, true)
		if (d.isValid()) return d.format('YYYY-MM-DD')
	}
	return null
}

function normalizeWeight(value) {
	if (value === null || value === undefined || value === '') return null
	const n = parseFloat(String(value).replace(/[^\d.-]/g, ''))
	if (Number.isNaN(n)) return null
	if (n <= 0 || n > 999.99) return null
	return Math.round(n * 100) / 100
}

// --- Parse CSV file using Papa ---

function parseFile(file) {
	return new Promise((resolve, reject) => {
		Papa.parse(file, {
			header: true,
			skipEmptyLines: 'greedy',
			transformHeader: normalizeHeader,
			complete: (results) => resolve(results),
			error: (err) => reject(err)
		})
	})
}

// Returns array of unknown column names (not in expectedColumns)
function findUnknownColumns(parsedFields, expectedColumns) {
	if (!parsedFields) return []
	return parsedFields.filter(f => f && !expectedColumns.includes(f))
}

// Returns array of required columns missing from the header
function findMissingRequired(parsedFields, requiredColumns) {
	const set = new Set(parsedFields || [])
	return requiredColumns.filter(c => !set.has(c))
}

// --- Clients CSV ---

export async function parseClientsCsv(file) {
	let parsed
	try {
		parsed = await parseFile(file)
	} catch (e) {
		return {
			fatalError: `Failed to read CSV: ${e.message || 'Unknown error'}`
		}
	}

	const fields = parsed.meta?.fields || []
	const missing = findMissingRequired(fields, REQUIRED_CLIENT_COLUMNS)
	if (missing.length) {
		return {
			fatalError: `clients.csv is missing required columns: ${missing.join(', ')}. Use the provided template.`
		}
	}

	const unknown = findUnknownColumns(fields, CLIENT_COLUMNS)

	const rawRows = parsed.data || []
	if (rawRows.length === 0) {
		return { fatalError: 'clients.csv has no rows.' }
	}
	if (rawRows.length > MAX_ROWS_PER_FILE) {
		return {
			fatalError: `clients.csv has ${rawRows.length} rows; the maximum allowed is ${MAX_ROWS_PER_FILE}.`
		}
	}

	const errors = []
	const warnings = []
	const seenOwnerIds = new Map() // owner_id -> first row number
	const rows = []

	rawRows.forEach((raw, idx) => {
		const rowNum = idx + 2 // +1 for header, +1 for 1-based

		const ownerId = trimOrNull(raw.owner_id)
		const firstName = trimOrNull(raw.first_name)
		const email = trimOrNull(raw.email)
		const rawPhone = trimOrNull(raw.phone)

		if (!ownerId) {
			errors.push({ row: rowNum, message: 'owner_id is required' })
		}
		if (!firstName) {
			errors.push({ row: rowNum, message: 'first_name is required' })
		}

		if (ownerId) {
			if (seenOwnerIds.has(ownerId)) {
				errors.push({
					row: rowNum,
					message: `Duplicate owner_id "${ownerId}" (also on row ${seenOwnerIds.get(ownerId)})`
				})
			} else {
				seenOwnerIds.set(ownerId, rowNum)
			}
		}

		if (email && !isValidEmail(email)) {
			errors.push({ row: rowNum, message: `Invalid email format: "${email}"` })
		}

		let formattedPhone = null
		if (rawPhone) {
			formattedPhone = formatPhone(rawPhone)
			if (!formattedPhone) {
				errors.push({
					row: rowNum,
					message: `Phone must be 10 digits: "${rawPhone}"`
				})
			}
		}

		rows.push({
			rowNum,
			ownerId,
			data: {
				name: firstName,
				last_name: trimOrNull(raw.last_name),
				email,
				phone_number: formattedPhone,
				address: trimOrNull(raw.address),
				address_2: trimOrNull(raw.address_2),
				city: trimOrNull(raw.city),
				state: trimOrNull(raw.state),
				zip: trimOrNull(raw.zip)
			}
		})
	})

	if (unknown.length) {
		warnings.push({
			message: `Unknown column(s) in clients.csv will be ignored: ${unknown.join(', ')}`
		})
	}

	return { rows, errors, warnings }
}

// --- Pets CSV ---

export async function parsePetsCsv(file, validOwnerIds = null) {
	let parsed
	try {
		parsed = await parseFile(file)
	} catch (e) {
		return {
			fatalError: `Failed to read CSV: ${e.message || 'Unknown error'}`
		}
	}

	const fields = parsed.meta?.fields || []
	const missing = findMissingRequired(fields, REQUIRED_PET_COLUMNS)
	if (missing.length) {
		return {
			fatalError: `pets.csv is missing required columns: ${missing.join(', ')}. Use the provided template.`
		}
	}

	const unknown = findUnknownColumns(fields, PET_COLUMNS)

	const rawRows = parsed.data || []
	if (rawRows.length === 0) {
		return { fatalError: 'pets.csv has no rows.' }
	}
	if (rawRows.length > MAX_ROWS_PER_FILE) {
		return {
			fatalError: `pets.csv has ${rawRows.length} rows; the maximum allowed is ${MAX_ROWS_PER_FILE}.`
		}
	}

	const errors = []
	const warnings = []
	const rows = []

	rawRows.forEach((raw, idx) => {
		const rowNum = idx + 2

		const ownerId = trimOrNull(raw.owner_id)
		const petName = trimOrNull(raw.pet_name)
		const rawSpecies = trimOrNull(raw.species)
		const rawBreed = trimOrNull(raw.breed)
		const rawSex = trimOrNull(raw.sex)
		const rawBirthday = trimOrNull(raw.birthday)
		const rawWeight = trimOrNull(raw.weight_lbs)

		if (!ownerId) {
			errors.push({ row: rowNum, message: 'owner_id is required' })
		} else if (validOwnerIds && !validOwnerIds.has(ownerId)) {
			errors.push({
				row: rowNum,
				message: `owner_id "${ownerId}" does not match any row in clients.csv`
			})
		}

		if (!petName) {
			errors.push({ row: rowNum, message: 'pet_name is required' })
		}

		const species = normalizeSpecies(rawSpecies)
		if (!rawSpecies) {
			errors.push({ row: rowNum, message: 'species is required' })
		} else if (!species) {
			errors.push({
				row: rowNum,
				message: `species must be one of: ${petTypes.join(', ')} (got "${rawSpecies}")`
			})
		}

		const breed = normalizeBreed(rawBreed, species)
		if (rawBreed && species && !isKnownBreed(rawBreed, species)) {
			warnings.push({
				row: rowNum,
				message: `Breed "${rawBreed}" is not in the standard ${species} breed list; it will be imported as-is.`
			})
		}

		let sex = null
		if (rawSex) {
			sex = normalizeSex(rawSex)
			if (!sex) {
				errors.push({
					row: rowNum,
					message: `sex must be Male or Female (got "${rawSex}")`
				})
			}
		}

		let birthday = null
		if (rawBirthday) {
			birthday = normalizeBirthday(rawBirthday)
			if (!birthday) {
				errors.push({
					row: rowNum,
					message: `birthday must be YYYY-MM-DD or MM/DD/YYYY (got "${rawBirthday}")`
				})
			}
		}

		let weight = null
		if (rawWeight) {
			weight = normalizeWeight(rawWeight)
			if (weight === null) {
				errors.push({
					row: rowNum,
					message: `weight_lbs must be a positive number under 1000 (got "${rawWeight}")`
				})
			}
		}

		rows.push({
			rowNum,
			ownerId,
			data: {
				name: petName,
				species,
				breed,
				birthday,
				sex,
				weight,
				medical_info: trimOrNull(raw.medical_info),
				grooming_info: trimOrNull(raw.grooming_info),
				personality: trimOrNull(raw.personality)
			}
		})
	})

	if (unknown.length) {
		warnings.push({
			message: `Unknown column(s) in pets.csv will be ignored: ${unknown.join(', ')}`
		})
	}

	return { rows, errors, warnings }
}

// --- Build DB-ready insert payloads ---

// Default reminder preferences for imported clients
const DEFAULT_REMINDER_PREFS = {
	email_reminders: true,
	sms_reminders: true
}

export function buildClientInsertRows(parsedClientRows, merchantId) {
	return parsedClientRows.map(r => ({
		merchant_id: merchantId,
		...r.data,
		...DEFAULT_REMINDER_PREFS
	}))
}

// Maps each pet to its newly-inserted client_id using ownerIdToDbId
// Throws if any pet's ownerId is not in the map (caller should already have validated)
export function buildPetInsertRows(parsedPetRows, ownerIdToDbId) {
	return parsedPetRows.map(r => {
		const clientId = ownerIdToDbId.get(r.ownerId)
		if (!clientId) {
			throw new Error(`No matching client for owner_id "${r.ownerId}"`)
		}
		return {
			client_id: clientId,
			...r.data
		}
	})
}

// Counts pets per ownerId
export function countPetsPerOwner(parsedPetRows) {
	const counts = new Map()
	parsedPetRows.forEach(r => {
		if (!r.ownerId) return
		counts.set(r.ownerId, (counts.get(r.ownerId) || 0) + 1)
	})
	return counts
}
